import { useState } from 'react';
import axios from 'axios';
import { Seo, CONTACT_EMAIL } from '../components/Seo';
import { Breadcrumbs } from '../components/Breadcrumbs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: '', email: '', betreff: '', nachricht: '', datenschutz: false, website: '' };

export default function Kontakt() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (form.name.trim().length < 2) err.name = 'Bitte gib deinen Namen ein (mindestens 2 Zeichen).';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = 'Bitte gib eine gültige E-Mail-Adresse ein.';
    if (form.betreff.trim().length < 3) err.betreff = 'Bitte gib einen Betreff ein (mindestens 3 Zeichen).';
    if (form.nachricht.trim().length < 10) err.nachricht = 'Bitte beschreibe dein Anliegen etwas genauer (mindestens 10 Zeichen).';
    if (!form.datenschutz) err.datenschutz = 'Bitte bestätige, dass du die Datenschutzerklärung gelesen hast.';
    return err;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length > 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setSuccess(res.data.message);
      setForm(initialForm);
    } catch {
      setErrors({ form: 'Das Senden ist leider fehlgeschlagen. Bitte prüfe deine Angaben und versuche es erneut.' });
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (key) =>
    `min-h-12 w-full rounded-sm border bg-white px-4 text-brand-brown transition-colors duration-200 ${
      errors[key] ? 'border-red-700' : 'border-brand-brown/25 hover:border-brand-brown/50'
    }`;

  return (
    <>
      <Seo
        title="Kontakt | Felix Genuss"
        description="Kontakt zu Felix Genuss: Fragen zu Rezepten, Anregungen oder Feedback – schreib uns über das Kontaktformular."
        path="/kontakt"
      />
      <Breadcrumbs items={[{ label: 'Startseite', href: '/' }, { label: 'Kontakt' }]} />

      <div className="container-site max-w-3xl pt-10">
        <p className="eyebrow">Schreib uns</p>
        <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
          Kontakt zu Felix Genuss
        </h1>
        <p className="mt-6 leading-relaxed text-brand-brown/85">
          Du hast eine Frage zu einem Rezept, eine Anregung oder möchtest einfach Grüsse an Felix dalassen? Wir freuen uns über deine Nachricht.
        </p>
      </div>

      <section className="container-site mt-10 max-w-3xl" aria-labelledby="direkter-kontakt-heading">
        <div className="border border-brand-gold/30 bg-brand-beige p-7" data-testid="direct-contact-block">
          <h2 id="direkter-kontakt-heading" className="font-serif text-2xl font-bold">Direkter Kontakt</h2>
          <p className="mt-4 text-sm font-bold">Felix Genuss</p>
          <p className="mt-1 text-sm">
            E-Mail:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="link-inline" data-testid="contact-email-link">
              {CONTACT_EMAIL}
            </a>
          </p>
          <p className="mt-4 text-sm leading-relaxed text-brand-brown/85">
            Du hast eine Frage zu Felix Genuss, unseren Rezepten oder einem Beitrag? Dann kannst du uns gerne über das Kontaktformular oder direkt per E-Mail kontaktieren.
          </p>
        </div>
      </section>

      <form onSubmit={onSubmit} noValidate className="container-site mt-12 max-w-3xl" data-testid="contact-form" aria-label="Kontaktformular">
        {errors.form && (
          <p role="alert" className="mb-6 rounded-sm border border-red-700 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800" data-testid="contact-form-error">
            {errors.form}
          </p>
        )}
        {success && (
          <p role="status" className="mb-6 rounded-sm border border-brand-olive bg-brand-beige px-4 py-3 text-sm font-semibold text-brand-olive-dark" data-testid="contact-form-success">
            {success}
          </p>
        )}

        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="kontakt-name" className="mb-2 block text-sm font-semibold">Name</label>
            <input
              id="kontakt-name" name="name" type="text" autoComplete="name" required
              value={form.name} onChange={set('name')}
              aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'fehler-name' : undefined}
              className={inputClass('name')} data-testid="contact-name-input"
            />
            {errors.name && <p id="fehler-name" role="alert" className="mt-2 text-sm font-semibold text-red-800" data-testid="error-name">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor="kontakt-email" className="mb-2 block text-sm font-semibold">E-Mail</label>
            <input
              id="kontakt-email" name="email" type="email" autoComplete="email" required
              value={form.email} onChange={set('email')}
              aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'fehler-email' : undefined}
              className={inputClass('email')} data-testid="contact-email-input"
            />
            {errors.email && <p id="fehler-email" role="alert" className="mt-2 text-sm font-semibold text-red-800" data-testid="error-email">{errors.email}</p>}
          </div>
        </div>

        <div className="mt-6">
          <label htmlFor="kontakt-betreff" className="mb-2 block text-sm font-semibold">Betreff</label>
          <input
            id="kontakt-betreff" name="betreff" type="text" required
            value={form.betreff} onChange={set('betreff')}
            aria-invalid={Boolean(errors.betreff)} aria-describedby={errors.betreff ? 'fehler-betreff' : undefined}
            className={inputClass('betreff')} data-testid="contact-subject-input"
          />
          {errors.betreff && <p id="fehler-betreff" role="alert" className="mt-2 text-sm font-semibold text-red-800" data-testid="error-subject">{errors.betreff}</p>}
        </div>

        <div className="mt-6">
          <label htmlFor="kontakt-nachricht" className="mb-2 block text-sm font-semibold">Nachricht</label>
          <textarea
            id="kontakt-nachricht" name="nachricht" rows={7} required
            value={form.nachricht} onChange={set('nachricht')}
            aria-invalid={Boolean(errors.nachricht)} aria-describedby={errors.nachricht ? 'fehler-nachricht' : undefined}
            className={`w-full rounded-sm border bg-white px-4 py-3 text-brand-brown transition-colors duration-200 ${errors.nachricht ? 'border-red-700' : 'border-brand-brown/25 hover:border-brand-brown/50'}`}
            data-testid="contact-message-input"
          />
          {errors.nachricht && <p id="fehler-nachricht" role="alert" className="mt-2 text-sm font-semibold text-red-800" data-testid="error-message">{errors.nachricht}</p>}
        </div>

        <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
          <label htmlFor="kontakt-website">Website (bitte frei lassen)</label>
          <input id="kontakt-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
        </div>

        <div className="mt-6 flex items-start gap-3">
          <input
            id="kontakt-datenschutz" name="datenschutz" type="checkbox"
            checked={form.datenschutz} onChange={set('datenschutz')}
            aria-invalid={Boolean(errors.datenschutz)} aria-describedby={errors.datenschutz ? 'fehler-datenschutz' : 'hinweis-datenschutz'}
            className="mt-1 h-5 w-5 shrink-0 accent-brand-olive" data-testid="contact-privacy-checkbox"
          />
          <div>
            <label htmlFor="kontakt-datenschutz" className="text-sm leading-relaxed">
              Ich habe die <a href="/datenschutz" className="link-inline">Datenschutzerklärung</a> gelesen und bin mit der Verarbeitung meiner Angaben zur Beantwortung meiner Anfrage einverstanden.
            </label>
            <p id="hinweis-datenschutz" className="mt-1 text-xs text-brand-brown-soft">Deine Daten werden ausschliesslich zur Bearbeitung deiner Nachricht verwendet.</p>
            {errors.datenschutz && <p id="fehler-datenschutz" role="alert" className="mt-2 text-sm font-semibold text-red-800" data-testid="error-privacy">{errors.datenschutz}</p>}
          </div>
        </div>

        <button type="submit" disabled={loading} className="btn-primary mt-8 disabled:opacity-60" data-testid="contact-submit-button">
          {loading ? 'Wird gesendet …' : 'Nachricht senden'}
        </button>
      </form>
    </>
  );
}
